import { untranslated, useTranslation } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { reviewFlowsUrl } from "@Services/dataManager";
import { executeQuery, type QueryShape } from "@Services/queryBuilder";
import React, { useCallback, useEffect, useState, type MouseEvent, SyntheticEvent } from "react";
import { fetchReviewFlowLevels } from "@Components/SpaceSettings/UserAdministration/ReviewFlows/ReviewFlowHelper";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { createNewReview, getResourceDetails } from "./ReviewsHelper";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { FileManagerAsPopup } from "@Features/files";
import ReviewLevelTree from "@Components/SpaceSettings/UserAdministration/ReviewFlows/ReviewFlowReviewLevels/ReviewLevelTree";
import { defaultCreateReviewData } from "./ReviewsConstants";
import TransitionsDiagram from "@Components/SpaceSettings/UserAdministration/ReviewFlows/ReviewFlowTransitions/TransitionsDiagram";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import { IssuesGrid } from "@Features/issues";
import { issuesInFilesCount } from "@Features/issues/state/bcfData.actions";
import ChecklistAssignmentModal from "./ChecklistAssignmentModal";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { TextField } from "@mui/material";
import Text from "@CubeComponents/html/Text/Text";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import { defaultModulesMapper } from "@Helpers/constants";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import type { ReviewResourceToAttach } from "./ReviewsHelper";
import type { CreateReviewResponse } from "@ServerTypes/reviews/reviews.schema";
import type { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";

/**
 * SERVER GAP: `reviewFlowReviewLevelsServiceResponse` (reviewflows.schema.ts) is a bare
 * `statusResponse` — it declares no `result` at all — and `fetchReviewFlowLevels` is typed
 * `Promise<ApiResponse>` whose `data` is `any`. So the levels/checklists this wizard snapshots have no
 * server type; only the fields actually consumed here are declared.
 */
interface ReviewFlowLevelOption {
  _id?: string;
  serial?: number;
  title?: string;
}

interface ReviewFlowChecklistOption {
  _id?: string;
  title?: string;
}

/**
 * One row of the reviewFlows grid (`POST /reviewflows/reviewFlowsService`, whose `result` is also
 * `z.unknown()`). A `type` alias so it satisfies `AutoComplete`'s `Record<string, unknown>` constraint.
 */
type ReviewFlowOption = {
  _id?: ObjectIdString;
  name?: string;
};

/** One record handed back by the file-manager popup; only these two fields are inspected here. */
interface FileManagerSelection {
  versionNumber?: number;
  fileVersion?: string | null;
}

interface ReviewDataType {
  name: string;
  description: string;
  reviewFlow: {
    /** A ReviewFlow `_id`; sent as `createReviewRequest.reviewFlowId`. `null` until one is picked. */
    id: ObjectIdString | null;
    reviewLevels: ReviewFlowLevelOption[];
    checklists: ReviewFlowChecklistOption[];
  };
  /**
   * KNOWN BUG, preserved verbatim: this was declared required but is ABSENT from the initial state (it
   * is not in `defaultCreateReviewData`), so it is `undefined` until the file/issue step populates it.
   * Marked optional here to state that, with erased assertions at the unconditional reads. Does the
   * server read it? Yes — it becomes `createReviewRequest.resources`. Fix: seed it to `[]` in
   * `defaultCreateReviewData` (a runtime change, so not done here).
   */
  resources?: ReviewResourceToAttach[];
  hasIssues: boolean;
  stage: number;
  attachIssues?: boolean;
}

interface CreateReviewModalProps {
  closeModal: () => void;
  refreshGrid: () => void;
  spaceId?: string;
  projectId: string;
  /** Pre-selected file-manager records handed straight to `getResourceDetails`. */
  initialFiles?: readonly unknown[];
}

const CreateReviewModal: React.FC<CreateReviewModalProps> = ({
  closeModal,
  refreshGrid,
  spaceId = "",
  projectId,
  initialFiles = [],
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [reviewData, setReviewData] = useState<ReviewDataType>({
    ...defaultCreateReviewData,
    reviewFlow: {
      // `defaultCreateReviewData.reviewFlow` is only `{ id: null }`; the levels and checklists are
      // wizard display state, filled in by `changeReviewFlowHandler` once a reviewFlow is picked.
      id: defaultCreateReviewData.reviewFlow.id,
      reviewLevels: [],
      checklists: [],
    },
    hasIssues: false,
  });

  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("levels");
  // Entry point: the `spaceId` prop is a plain `string` and is later replaced from the SelectSpaces
  // callback, which hands back a branded id. Parsed once here so "" (the pre-existing placeholder for
  // "no space yet") stays the only unbranded value, and `!defaultSpaceID` narrows it away at each use.
  const [defaultSpaceID, setDefaultSpaceID] = useState<ObjectIdString | "">(
    objectIdString.safeParse(spaceId).data ?? "",
  );
  const [isLoadingCreateButton, setIsLoadingCreateButton] = useState<boolean>(false);
  const [reviewFlows, setReviewFlows] = useState<ReviewFlowOption[]>([]);
  const [selectedReviewFlow, setSelectedReviewFlow] = useState<ReviewFlowOption | null>(null);
  const closeReviewCreation = () => closeModal();

  const fetchReviewFlows = useCallback(async () => {
    if (!defaultSpaceID) return;

    const reviewFlowsQuery: QueryShape = {
      params: { spaceId: defaultSpaceID, filterByInitiator: true },
      where: [{ field: "archivedStatus", operator: "equal", value: 0 }],
    };

    const result = await executeQuery<ReviewFlowOption>(reviewFlowsUrl, reviewFlowsQuery);
    setReviewFlows(result.result);
  }, [defaultSpaceID]);

  useEffect(() => {
    fetchReviewFlows();
  }, [fetchReviewFlows, reviewData.stage]);

  const handleToastNotification = (data: CreateReviewResponse) => {
    if (data.status) {
      dispatch(
        showToastNotification({
          content: t("The review has been created successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          // The raw server `errorMessage` is server copy, not a translation key, so it goes through
          // `untranslated()` like every other toast in this feature.
          content:
            data.errorMessage === undefined ? t("The review could not be created") : untranslated(data.errorMessage),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const attachIssuesRadioBtnCallback = (args: boolean) => {
    setReviewData((prev) => ({ ...prev, attachIssues: args }));
  };

  const resourceSelectionCallback = async (listOfFiles: Record<string, FileManagerSelection>) => {
    const filesArray = Object.values(listOfFiles);
    const filesWithoutVersions = filesArray.filter((file) => file.versionNumber === 0 || file.fileVersion == null);

    if (filesWithoutVersions.length > 0) {
      dispatch(
        showToastNotification({
          content: t(`Please select files having a version.`),
          type: TOAST_TYPE_ERROR,
        }),
      );
      setIsLoadingCreateButton(false);
      return;
    }

    const resources = filesArray;
    const resourcesMap = getResourceDetails(resources);

    if (!resourcesMap.length) {
      dispatch(showToastNotification({ content: t("Please add at least one file"), type: TOAST_TYPE_ERROR }));
      setReviewData((prev) => ({ ...prev, stage: 1 }));
      return;
    }

    const fileIds = resourcesMap
      .filter(({ resourceType }) => resourceType === "RESOURCE_TYPE_FILE" || resourceType === "RESOURCE_TYPE_EMBED")
      // `fileVersionId` is optional on the resource descriptor even though every RESOURCE_TYPE_FILE /
      // RESOURCE_TYPE_EMBED entry `getResourceDetails` produces carries one, so it is dropped rather
      // than asserted — an `undefined` id would otherwise be sent to `issuesInFilesCount`.
      .map((resource) => resource.fileVersionId)
      .filter((fileVersionId) => fileVersionId !== undefined);

    const data = { value: { params: { fileId: fileIds }, skip: 0, take: 1 } };

    let containsIssues = false;
    try {
      containsIssues = !!(await issuesInFilesCount(data));
    } catch (error) {
      containsIssues = false;
      Promise.reject(error);
    }

    const hasChecklist =
      Array.isArray(reviewData.reviewFlow.checklists) &&
      reviewData.reviewFlow.checklists.length > 0 &&
      Array.isArray(reviewData.reviewFlow.reviewLevels) &&
      reviewData.reviewFlow.reviewLevels.length > 0;

    if (hasChecklist) {
      setIsLoadingCreateButton(false);
      setReviewData((prev) => ({
        ...prev,
        resources: resourcesMap,
        hasIssues: containsIssues,
        stage: 3,
      }));
    } else if (containsIssues) {
      setIsLoadingCreateButton(false);
      setReviewData((prev) => ({
        ...prev,
        resources: resourcesMap,
        hasIssues: true,
        stage: 4,
      }));
    } else {
      setIsLoadingCreateButton(true);
      await createReview(resourcesMap);
    }
  };

  const createReview = async (resourcesMap?: ReviewResourceToAttach[]) => {
    const { name, description, reviewFlow: reviewFlow, resources } = reviewData;
    const reviewFlowId = reviewFlow.id;
    // No space means no reviewFlow list, so `reviewFlowId` is already null on that path; the second
    // half of the guard is what narrows the "" placeholder off `defaultSpaceID` for the payload.
    if (!reviewFlowId || !defaultSpaceID) return;
    const data = {
      name,
      spaceId: defaultSpaceID,
      reviewFlowId,
      description,
      // `createNewReview` takes `z.input<typeof createReviewRequest>`, so `resources` is optional here
      // (it carries `.default([])`) and `undefined` is a legal payload — no assertion needed.
      resources: resourcesMap ?? resources,
    };

    const response = await createNewReview(data);
    setIsLoadingCreateButton(false);
    handleToastNotification(response.data);
    refreshGrid();
    closeModal();
  };

  const moveToPreviousSection = () => {
    setReviewData((prev) => ({ ...prev, stage: 1 }));
  };

  const moveToNextSection = () => {
    if (initialFiles.length > 0) {
      const resourcesMap = getResourceDetails(initialFiles);

      const hasChecklist = reviewData.reviewFlow.checklists.length;
      if (hasChecklist) {
        setReviewData((prev) => ({ ...prev, resources: resourcesMap, stage: 3 }));
      } else {
        setIsLoadingCreateButton(true);
        createReview(resourcesMap);
      }
    } else {
      setReviewData((prev) => ({ ...prev, stage: 2 }));
    }
  };

  const fetchReviewFlowLevelDetails = async (reviewFlowId: string) => {
    const res = await fetchReviewFlowLevels({
      value: { skip: 0, take: 1000, params: { reviewFlowId } },
    });
    return { levels: res.data.result?.levels ?? [], checklists: res.data.result?.checklists ?? [] };
  };

  const changeReviewFlowHandler = async (event: SyntheticEvent, newValue: ReviewFlowOption | null) => {
    if (!newValue) {
      setSelectedReviewFlow(null);
      setReviewData((prev) => ({
        ...prev,
        reviewFlow: {
          id: null,
          reviewLevels: [],
          checklists: [],
        },
      }));
      return;
    }

    setIsDataLoading(true);
    setSelectedReviewFlow(newValue);
    const { levels, checklists } = await fetchReviewFlowLevelDetails(newValue._id!);
    setReviewData((prev) => ({
      ...prev,
      reviewFlow: {
        id: newValue._id!,
        reviewLevels: levels,
        checklists,
      },
    }));
    setIsDataLoading(false);
  };

  const handleTabSwitch = (e: MouseEvent<HTMLElement>) => {
    const target = e.currentTarget.getAttribute("data-name");
    if (target !== null) {
      setActiveTab(target);
    }
  };

  const confirmAttachIssuesCallback = () => {
    if (!reviewData.attachIssues) {
      setIsLoadingCreateButton(true);
      createReview();
      return;
    }
    setReviewData((prev) => ({ ...prev, stage: 5 }));
  };

  const issueSelectionCallback = (issues: { id: ObjectIdString }[]) => {
    const issuesListForReview: ReviewResourceToAttach[] = issues.map((issue) => ({
      resourceType: "RESOURCE_TYPE_ISSUE",
      issueId: issue.id,
    }));

    setReviewData((prev) => {
      const resourcesExceptIssues = prev.resources!.filter(
        ({ resourceType }) => resourceType !== "RESOURCE_TYPE_ISSUE",
      );
      return { ...prev, resources: [...resourcesExceptIssues, ...issuesListForReview] };
    });
  };

  const attachIssuesToReviewCallback = () => {
    setIsLoadingCreateButton(true);
    createReview();
  };

  const spacesDropDownList = () => (
    <SelectSpaces
      moduleName={defaultModulesMapper.REVIEWS}
      data-testid="spaceDropdownField"
      allowBorder={true}
      isDisabled={!!spaceId}
      projectId={projectId}
      workspaceId={spaceId}
      selectedSpaceCallback={(params: { _id: ObjectIdString }) => {
        setDefaultSpaceID(params._id);
        setSelectedReviewFlow(null);
        setReviewData((prev) => ({ ...prev, reviewFlow: { ...prev.reviewFlow, id: null } }));
      }}
    />
  );

  const header = () => (
    <div className="flex items-center justify-between w-full rounded-t-lg">
      <p className="text-2xl font-bold">
        <Text>{t("Create Review")}</Text>
      </p>
      <div className="flex items-center space-inline-2">{spacesDropDownList()}</div>
    </div>
  );

  const content = () => (
    <div className="p-6 px-8">
      <div className="flex flex-col justify-start gap-6 mb-8">
        <div className="flex flex-row items-center justify-between">
          <p className="text-base font-semibold">
            <Text>{t("Review Name")}</Text>
          </p>
          <div className="w-full max-w-[43.75rem]">
            <TextFieldComponent
              placeholder={t("Enter Review Name")}
              onChange={(e) => setReviewData((prev) => ({ ...prev, name: e.target.value }))}
              value={reviewData.name}
            />
          </div>
        </div>

        <div className="flex flex-row items-center justify-between">
          <p className="text-base font-semibold">
            <Text>{t("Review Description")}</Text>
          </p>
          <div className="w-full max-w-[43.75rem]">
            <TextFieldComponent
              onChange={(e) => setReviewData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder={t("Enter Review Description")}
              value={reviewData.description}
            />
          </div>
        </div>

        <div className="flex flex-row items-center justify-between">
          <p className="text-base font-semibold">
            <Text>{t("Select Review Flow")}</Text>
          </p>
          <div className="w-full max-w-[43.75rem]">
            <AutoComplete
              options={reviewFlows}
              value={selectedReviewFlow}
              onChange={changeReviewFlowHandler}
              getOptionLabel={(option) => option.name!}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField {...params} placeholder={t("Select Review Flow")} />
              )}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="flex flex-row items-center justify-start gap-8 ps-8 pb-0.5 border border-[#ecf3fe]">
          <button
            data-name="levels"
            className="flex flex-col cursor-pointer h-14 justify-end [&_span]:leading-[2.75rem] [&_span]:text-base [&_span]:font-semibold"
            onClick={handleTabSwitch}
            type="button"
          >
            <span>
              <Text>{t("Review Levels")}</Text>
            </span>
            <div
              className={
                activeTab === "levels"
                  ? "rounded-md bg-[linear-gradient(96deg,_var(--primary-gradient-color1)_0%,_var(--primary-gradient-color2)_110%)] h-1"
                  : "bg-inherit h-1"
              }
            />
          </button>
          <button
            data-name="diagram"
            className="flex flex-col cursor-pointer h-14 justify-end [&_span]:leading-[2.75rem] [&_span]:text-base [&_span]:font-semibold"
            onClick={handleTabSwitch}
            type="button"
          >
            <span>
              <Text>{t("Transition Diagram")}</Text>
            </span>
            <div
              className={
                activeTab === "diagram"
                  ? "rounded-md bg-[linear-gradient(96deg,_var(--primary-gradient-color1)_0%,_var(--primary-gradient-color2)_110%)] h-1"
                  : "bg-inherit h-1"
              }
            />
          </button>
        </div>

        <div className="pt-4 pb-6 ps-5 border border-[#ecf3fe] h-[28.125rem] mb-8 overflow-auto">
          {activeTab === "levels" && !isDataLoading && reviewData.reviewFlow.id && (
            <ReviewLevelTree
              reviewLevels={reviewData.reviewFlow.reviewLevels}
              changeLevelCheckboxStatusHandler={() => {}}
              changeReviewerCheckboxStatusHandler={() => {}}
              notifyUserInReviewCallback={() => {}}
            />
          )}
          {activeTab === "diagram" && !isDataLoading && reviewData.reviewFlow.id && (
            <TransitionsDiagram reviewFlowId={reviewData.reviewFlow.id} />
          )}
        </div>
      </div>
    </div>
  );

  const footer = () => (
    <div className="flex gap-4">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={moveToNextSection}
        disabled={!reviewData.name.trim() || !reviewData.reviewFlow.id}
        loading={isLoadingCreateButton}
      >
        <Text>{t("Next")}</Text>
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeModal}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  return (
    <div>
      {reviewData.stage === 1 && (
        <ModalStateDialogComponent
          isVisible
          width="62.5rem"
          allowModalPadding={false}
          headerTitle={header()}
          modelContent={content()}
          footerContent={footer()}
          onCancel={closeModal}
        />
      )}

      {reviewData.stage === 2 && (
        <FileManagerAsPopup
          projectId={projectId}
          workspaceId={defaultSpaceID}
          attachSelectedBtnName="Next"
          id="fileManagerAsPopup"
          handleFileSelectedButtonClick={resourceSelectionCallback}
          handleFileSelectedCancelClick={moveToPreviousSection}
          hideIssues
          hideTasks
          hideSchedules
          hideEstimates
          hideContracts
          hideClashTests
          hideMergeFiles
          hideShortcuts
          hideExternalSpace
          hideReviews
          openThisSpaceByDefault
          disableFiles={[]}
          minimumFiles={1}
          isLoadingCreateButton={isLoadingCreateButton}
          setIsLoadingCreateButton={setIsLoadingCreateButton}
          enableFilesTypes={["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_FOLDER", "RESOURCE_TYPE_EMBED"]}
        />
      )}

      {reviewData.stage === 3 && (
        <ChecklistAssignmentModal
          reviewData={reviewData}
          setReviewData={setReviewData}
          onNext={(updatedData) => {
            if (updatedData.hasIssues) {
              setIsLoadingCreateButton(false);
              setReviewData({ ...updatedData, stage: 4 });
            } else {
              setIsLoadingCreateButton(true);
              createReview(updatedData.resources);
            }
          }}
          onBack={() => {
            setIsLoadingCreateButton(false);
            setReviewData((prev) => ({ ...prev, stage: 2 }));
          }}
        />
      )}

      {reviewData.stage === 4 && (
        <ModalStateDialogComponent
          isVisible
          allowModalPadding={false}
          width="31.25rem"
          headerTitle={
            <p className="w-full text-2xl font-bold rounded-t-2">
              <Text>{t("Attach Issues")}</Text>
            </p>
          }
          modelContent={
            <div className="flex flex-col justify-start gap-6">
              <p>
                <Text>{t("The file(s) that you have selected contain(s) issue(s)")}</Text>
              </p>
              <div className="flex flex-col gap-3 pe-4">
                <RadioButton
                  onChange={() => attachIssuesRadioBtnCallback(true)}
                  label="Attach Issues"
                  checked={!!reviewData.attachIssues}
                />
                <RadioButton
                  onChange={() => attachIssuesRadioBtnCallback(false)}
                  label="Don't Attach Issues"
                  checked={!reviewData.attachIssues}
                />
              </div>
            </div>
          }
          footerContent={
            <div className="flex flex-row gap-4 items-center justify-start">
              <ButtonComponent
                onClick={confirmAttachIssuesCallback}
                buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                loading={isLoadingCreateButton}
              >
                <Text>{t("Confirm")}</Text>
              </ButtonComponent>
              <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeReviewCreation}>
                <Text>{t("Cancel")}</Text>
              </ButtonComponent>
            </div>
          }
          onCancel={closeReviewCreation}
        />
      )}

      {reviewData.stage === 5 && (
        <ModalStateDialogComponent
          isVisible
          allowModalPadding={false}
          width="62.5rem"
          headerTitle={
            <p className="w-full text-2xl font-bold rounded-t-2">
              <Text>{t("Attach Issues")}</Text>
            </p>
          }
          modelContent={
            <div className="flex flex-col h-600 relative w-full overflow-auto">
              <IssuesGrid
                showCheckboxes
                showStatusCol={false}
                disabledIssueIds={[]}
                getAllIssuesInSpace={defaultSpaceID}
                getStandaloneIssuesForSpaceId={defaultSpaceID}
                spaceId={defaultSpaceID}
                showTitleLine={false}
                allowOpenSidebar={false}
                allowTools={false}
                issueSelectionCallback={issueSelectionCallback}
                fileVersionIds={reviewData
                  .resources!.filter(
                    ({ resourceType }) =>
                      resourceType === "RESOURCE_TYPE_FILE" || resourceType === "RESOURCE_TYPE_EMBED",
                  )
                  .map((resource) => resource.fileVersionId)
                  .filter((fileVersionId) => fileVersionId !== undefined)}
              />
            </div>
          }
          footerContent={
            <div className="flex flex-row gap-4 items-center justify-start">
              <ButtonComponent
                onClick={attachIssuesToReviewCallback}
                buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                loading={isLoadingCreateButton}
              >
                <Text>{t("Attach Issues & Create Review")}</Text>
              </ButtonComponent>
              <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeReviewCreation}>
                <Text>{t("Cancel")}</Text>
              </ButtonComponent>
            </div>
          }
          onCancel={closeReviewCreation}
        />
      )}
    </div>
  );
};

export default CreateReviewModal;
